import React, { useEffect, useState } from 'react';
import { type VariantProps } from 'class-variance-authority';
import { getUsersInitials, sidebarStyles } from './helper';
import { clsxMerge } from '../../utils/classNameUtils';
import { canShow, hasPermission } from '../dashboard/dashboardpage/utils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { AccessPermissions } from '../../interfaces/User';
import { FrontEdgeLogo } from '../icons/FrontEdgeLogo';
import { ChevronDownIcon } from '../icons/ChevronDownIcon';
import { ChevronRightIcon } from '../icons/ChevronRightIcon';
import { LogoutIcon } from '../icons/LogoutIcon';
import { AppState, setShowDrawer } from '../../store/appSlice/appSlice';
import { useMediaQuery, useTheme } from '@mui/material';

export interface SidebarItem {
    label: string;
    icon?: React.ReactNode;
    pathName: string;
    onClick?: () => void;
    submenu?: SidebarItem[];
    feature: string;
    visible?: boolean;
    permissionTitle?: string;
    level: number[];
    hideLayout?: boolean;
}

export interface User {
    avatarUrl?: string | React.ReactNode;
    name: string;
    onClick?: () => void;
}

export interface SidebarProps extends VariantProps<typeof sidebarStyles> {
    className?: string;
    sideBarItems: SidebarItem[];
    user: User;
    activePath: string;
    permissions: AccessPermissions;
}

const Sidebar = ({
    className,
    sideBarItems,
    user,
    activePath,
    permissions
}: SidebarProps) => {
    const dispatch = useDispatch();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [openSubmenus, setOpenSubmenus] = useState<{
        [key: string]: boolean;
    }>({});
    const [isAvatarError, setIsAvatarError] = useState(false);

    const { user: userState, token } = useSelector((state: RootState) => ({
        user: state.user.user,
        token: state.user.token
    }));

    const { showDrawer } = useSelector((state: { app: AppState }) => state.app);

    const toggleDrawer = () => {
        if (isMobile) {
            dispatch(setShowDrawer(!showDrawer));
        }
    };

    useEffect(() => {
        const openMenus = sideBarItems.reduce(
            (acc, item) => {
                if (item.submenu) {
                    const isActive = item.submenu.some((subItem) =>
                        activePath.startsWith(subItem.pathName)
                    );
                    if (isActive) {
                        acc[item.label] = true;
                    }
                }
                return acc;
            },
            {} as { [key: string]: boolean }
        );

        setOpenSubmenus((prev) => ({ ...prev, ...openMenus }));
    }, [activePath, sideBarItems]);

    const handleItemClick = (item: SidebarItem) => {
        if (item.submenu) {
            toggleSubmenu(item.label);
        } else if (item.onClick) {
            toggleDrawer();
            item.onClick();
        }
    };

    const toggleSubmenu = (label: string) => {
        setOpenSubmenus((prev) => ({ ...prev, [label]: !prev[label] }));
    };

    const handleAvatarError = () => {
        setIsAvatarError(true);
    };

    return (
        <div className={clsxMerge(sidebarStyles(), className)}>
            <div className='p-4'>
                <FrontEdgeLogo />
            </div>
            <div className='flex-1 overflow-y-auto px-4'>
                <ul className='space-y-2'>
                    {sideBarItems
                        .filter(
                            (item) =>
                                canShow(userState, item) &&
                                hasPermission({
                                    userPermissions: permissions,
                                    module: item?.permissionTitle ?? '',
                                    permissionList: ['view'],
                                    feature: item.feature
                                })
                        )
                        .map((item) => (
                            item.visible !== false &&
                            <li key={item.label} className='cursor-pointer'>
                                <div
                                    onClick={() => handleItemClick(item)}
                                    className={clsxMerge(
                                        'flex items-center p-3 hover:bg-[#F9FAFB] rounded-md',
                                        {
                                            'bg-[#F9FAFB] font-semibold':
                                                activePath.startsWith(
                                                    item.pathName
                                                ) ||
                                                (item.submenu &&
                                                    item.submenu.some(
                                                        (subItem) =>
                                                            activePath.startsWith(
                                                                subItem.pathName
                                                            )
                                                    ))
                                        }
                                    )}
                                >
                                    {item.icon}
                                    <span className='ml-4'>{item.label}</span>
                                    {item.submenu && (
                                        <span className='ml-auto'>
                                            {openSubmenus[item.label] ? (
                                                <ChevronDownIcon />
                                            ) : (
                                                <ChevronRightIcon />
                                            )}
                                        </span>
                                    )}
                                </div>
                                {item.submenu && openSubmenus[item.label] && (
                                    <ul className=' mt-2 mb-3'>
                                        {item.submenu
                                            .filter(
                                                (item) =>
                                                    canShow(userState, item) &&
                                                    hasPermission({
                                                        userPermissions:
                                                            permissions,
                                                        module:
                                                            item?.permissionTitle ??
                                                            '',
                                                        permissionList: [ 'view'],
                                                        feature: item.feature
                                                    })
                                            )
                                            .map((subItem) => (
                                                <li
                                                    key={subItem.label}
                                                    className={clsxMerge(
                                                        'pl-12 py-2 mb-1 cursor-pointer hover:bg-[#F9FAFB] rounded-md',
                                                        {
                                                            'bg-[#F9FAFB] font-semibold':
                                                                activePath ===
                                                                subItem.pathName
                                                        }
                                                    )}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (subItem.onClick)
                                                            subItem.onClick();
                                                        toggleDrawer();
                                                    }}
                                                >
                                                    <span>{subItem.label}</span>
                                                </li>
                                            ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                </ul>
            </div>
            <div
                className='p-4 border-[#EAECF0] border-t text-center flex items-center justify-between cursor-pointer'
                onClick={(e) => {
                    e.stopPropagation();
                    if (user.onClick) user.onClick();
                }}
            >
                <div className='flex items-center gap-3'>
                    {!isAvatarError && user.avatarUrl ? (
                        typeof user.avatarUrl === 'string' ? (
                            <img
                                src={user.avatarUrl}
                                alt='User Avatar'
                                className='w-10 h-10 rounded-full'
                                onError={handleAvatarError}
                            />
                        ) : (
                            <div className='w-10 h-10 rounded-full bg-gray-500 flex items-center justify-center'>
                                {user.avatarUrl}
                            </div>
                        )
                    ) : (
                        <div className='w-10 h-10 text-midnightHaze rounded-full flex items-center justify-center bg-athensGray'>
                            {getUsersInitials(user.name)}
                        </div>
                    )}
                    <span className='font-bold text-sm overflow-hidden w-[70%] text-ellipsis whitespace-nowrap'>
                        {user.name}
                    </span>
                </div>

                <div>
                    <LogoutIcon />
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
